// UP6: the chosen sound, played through Web Audio only after a person's
// gesture unlocked the context (browsers refuse autoplay). Decodes each
// bundled CC0 file once; a decode failure evicts the cache entry so the next
// event retries. Off is the resting state: nothing here runs unless the
// person turned sound on.
import type { SessionNotificationSound } from "@cleo/contracts";

/** The bundled CC0 files (apps/web/public/sounds/LICENSE-CC0.md). */
export const SESSION_NOTIFICATION_SOUND_URLS: Readonly<Record<SessionNotificationSound, string>> = {
  "soft-chime": "/sounds/soft-chime.wav",
  "two-tone": "/sounds/two-tone.wav",
};

export const SESSION_NOTIFICATION_SOUND_LABELS: Readonly<Record<SessionNotificationSound, string>> =
  {
    "soft-chime": "Soft chime",
    "two-tone": "Two-tone",
  };

/** Completion plays the chosen sound; attention plays the other, so the ear tells them apart. */
export function soundForEvent(
  chosen: SessionNotificationSound,
  kind: "completion" | "attention",
): SessionNotificationSound {
  if (kind === "completion") return chosen;
  return chosen === "soft-chime" ? "two-tone" : "soft-chime";
}

interface AudioLike {
  readonly state: string;
  resume(): Promise<void>;
  decodeAudioData(data: ArrayBuffer): Promise<AudioBuffer>;
  createBufferSource(): AudioBufferSourceNode;
  readonly destination: AudioNode;
  readonly currentTime: number;
}

let context: AudioLike | null = null;
const buffers = new Map<string, Promise<AudioBuffer>>();

function audioContext(): AudioLike | null {
  if (context !== null) return context;
  const Ctor = (globalThis as { AudioContext?: new () => AudioLike }).AudioContext;
  if (Ctor === undefined) return null;
  try {
    context = new Ctor();
  } catch {
    context = null;
  }
  return context;
}

/** Call from a pointerdown/keydown handler: creates or resumes the context. */
export async function unlockSessionNotificationAudio(): Promise<boolean> {
  const audio = audioContext();
  if (audio === null) return false;
  if (audio.state !== "running") {
    try {
      await audio.resume();
    } catch {
      return false;
    }
  }
  return audio.state === "running";
}

/**
 * Play one bundled sound. `shouldPlay` is re-read after the decode so a
 * person who turned sound off during the fetch hears nothing. Returns
 * whether a source actually started.
 */
export async function playSessionNotificationSound(
  sound: SessionNotificationSound,
  shouldPlay: () => boolean = () => true,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  const audio = audioContext();
  if (audio === null || audio.state !== "running") return false;
  const url = SESSION_NOTIFICATION_SOUND_URLS[sound];
  let decoding = buffers.get(url);
  if (decoding === undefined) {
    decoding = fetchImpl(url)
      .then((response) => {
        if (!response.ok) throw new Error(`sound ${url} HTTP ${response.status}`);
        return response.arrayBuffer();
      })
      .then((bytes) => audio.decodeAudioData(bytes));
    buffers.set(url, decoding);
  }
  let buffer: AudioBuffer;
  try {
    buffer = await decoding;
  } catch {
    buffers.delete(url);
    return false;
  }
  if (!shouldPlay()) return false;
  try {
    const source = audio.createBufferSource();
    source.buffer = buffer;
    source.connect(audio.destination);
    source.start(audio.currentTime);
    return true;
  } catch {
    return false;
  }
}

/** Test seam: forget the context and decoded buffers. */
export function resetSessionNotificationAudioForTests(): void {
  context = null;
  buffers.clear();
}
